import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { dnsAPI, domainsAPI } from '@hpanel/web-common/api/client'
import { DnsPublicationPanel } from '@hpanel/web-common/components/DnsPublicationPanel'
import type {
  DnsRecord,
  DnsRecordCreateRequest,
  Domain,
} from '@hpanel/web-common/api/types'

/**
 * Tenant DNS Editor (Plan 3).
 *
 * Pick a domain → list its records → add/delete. Validation lives
 * server-side; the UI shows the 400 messages verbatim so tenants
 * see "MX requires priority" / "CNAME at apex refused" / etc.
 */
const TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'] as const

const _emptyForm: DnsRecordCreateRequest = {
  type: 'A',
  name: '@',
  value: '',
  ttl: 3600,
}

export const DnsEditor: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedId = searchParams.get('domain')
  const [domains, setDomains] = useState<Domain[]>([])
  const [domainId, setDomainId] = useState<number | null>(null)
  const [records, setRecords] = useState<DnsRecord[]>([])
  const [form, setForm] = useState<DnsRecordCreateRequest>({ ..._emptyForm })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loadingRecords, setLoadingRecords] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await domainsAPI.list()
        if (cancelled) return
        setDomains(r.domains)
        const selected = requestedId === null ? r.domains[0] : r.domains.find(d => String(d.id) === requestedId)
        setDomainId(selected?.id ?? null)
        setError(!selected && requestedId !== null ? 'Requested domain is unavailable. Select another domain.' : null)
      } catch (e: unknown) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load domains')
      }
    })()
    return () => { cancelled = true }
  }, [requestedId])

  useEffect(() => {
    let cancelled = false
    setRecords([])
    setForm({ ..._emptyForm })
    if (domainId === null) return
    setLoadingRecords(true)
    setError(null)
    void (async () => {
      try {
        const rows = await dnsAPI.list(domainId)
        if (!cancelled) setRecords(rows)
      } catch (e: unknown) {
        if (cancelled) return
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load records')
      } finally {
        if (!cancelled) setLoadingRecords(false)
      }
    })()
    return () => { cancelled = true }
  }, [domainId])

  const refresh = async () => {
    if (domainId === null) return
    setRecords(await dnsAPI.list(domainId))
  }

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (domainId === null) return
    setBusy(true)
    setError(null)
    try {
      await dnsAPI.create(domainId, form)
      setForm({ ..._emptyForm })
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: number) => {
    if (domainId === null) return
    if (!window.confirm('Delete this DNS record?')) return
    setBusy(true)
    try {
      await dnsAPI.delete(domainId, id)
      await refresh()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  const showsPriority = ['MX', 'SRV'].includes(form.type)
  const showsSrv = form.type === 'SRV'
  const showsCaa = form.type === 'CAA'

  return (
    <div data-testid="dns-editor-page" className="space-y-6">
      <div>
        <h1 data-testid="page-title" className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          DNS
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Add A/AAAA/CNAME/MX/TXT/NS/SRV/CAA records. Validation runs
          server-side; bad records get a clear error message.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3 text-sm">
          {error}
        </div>
      )}

      {domainId !== null && !loadingRecords && <DnsPublicationPanel key={domainId} domainId={domainId} revision={records} />}

      {domains.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">
          You don't own any domains yet. Add one in <strong>Domains</strong>.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Domain
          </label>
          <select
            value={domainId ?? ''}
            onChange={(e) => setSearchParams({ domain: e.target.value })}
            data-testid="dns-domain-select"
            disabled={busy}
            aria-label="Domain"
            className="mt-1 w-full max-w-md px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
          >
            <option value="" disabled>Select a domain</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {domainId !== null && (
        <>
          <form
            onSubmit={create}
            data-testid="dns-record-form"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Add record
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({ ..._emptyForm, type: e.target.value })
                }
                data-testid="dns-record-type-select"
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              >
                {TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <input
                data-testid="dns-record-name-input"
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="name (e.g. @, www)"
                value={form.name ?? ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                data-testid="dns-record-value-input"
                className="col-span-2 px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="value"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                required
              />
              <input
                type="number"
                className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                placeholder="TTL"
                value={form.ttl ?? 3600}
                onChange={(e) =>
                  setForm({ ...form, ttl: Number(e.target.value) })
                }
              />
              {showsPriority && (
                <input
                  type="number"
                  className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                  placeholder="priority"
                  value={form.priority ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, priority: Number(e.target.value) })
                  }
                />
              )}
              {showsSrv && (
                <>
                  <input
                    type="number"
                    className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                    placeholder="weight"
                    value={form.weight ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, weight: Number(e.target.value) })
                    }
                  />
                  <input
                    type="number"
                    className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                    placeholder="port"
                    value={form.port ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, port: Number(e.target.value) })
                    }
                  />
                </>
              )}
              {showsCaa && (
                <>
                  <input
                    type="number"
                    className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                    placeholder="flag"
                    value={form.flag ?? 0}
                    onChange={(e) =>
                      setForm({ ...form, flag: Number(e.target.value) })
                    }
                  />
                  <input
                    className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
                    placeholder="tag (issue/issuewild/iodef)"
                    value={form.tag ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, tag: e.target.value })
                    }
                  />
                </>
              )}
            </div>
            <button
              type="submit"
              disabled={busy || loadingRecords}
              data-testid="dns-record-add-button"
              className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg"
            >
              {busy ? 'Adding…' : 'Add record'}
            </button>
          </form>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">TTL</th>
                  <th className="px-4 py-3">Extras</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      {loadingRecords ? 'Loading records…' : error ? 'Records unavailable.' : 'No records yet.'}
                    </td>
                  </tr>
                ) : (
                  records.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`dns-record-row-${r.id}`}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="px-4 py-3 font-mono">{r.type}</td>
                      <td className="px-4 py-3 font-mono">{r.name}</td>
                      <td className="px-4 py-3 font-mono break-all">
                        {r.value}
                      </td>
                      <td className="px-4 py-3">{r.ttl}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {r.priority !== null && `pri=${r.priority} `}
                        {r.weight !== null && `wt=${r.weight} `}
                        {r.port !== null && `port=${r.port} `}
                        {r.flag !== null && `flag=${r.flag} `}
                        {r.tag && `tag=${r.tag}`}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          disabled={busy || loadingRecords}
                          onClick={() => remove(r.id)}
                          data-testid={`dns-record-delete-${r.id}`}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
